import { getApiClient } from "@punchup/utils/apiRegistry";
import { readNdjson } from "@punchup/utils/ndjson";
import type {
  SupportAnswerRating,
  SupportChatEvent,
  SupportConversationDTO,
  SupportConversationStatus,
  SupportConversationSummaryDTO,
  SupportGapsDTO,
} from "../../models/support";

const BASE = "/api/v2/internal/support";

/**
 * Asks the support assistant a question and yields its NDJSON events as they
 * arrive. Aborting `signal` (or stopping the iteration) cancels the request.
 */
export async function* streamSupportChat(
  body: { question: string; conversationId?: string },
  options: { signal?: AbortSignal } = {}
): AsyncGenerator<SupportChatEvent, void, undefined> {
  const stream = await getApiClient().stream(`${BASE}/chat`, body, {
    signal: options.signal,
  });
  yield* readNdjson<SupportChatEvent>(stream);
}

export async function fetchSupportConversations(): Promise<
  SupportConversationSummaryDTO[]
> {
  const response = await getApiClient().get<{
    data: { conversations: SupportConversationSummaryDTO[] };
  }>(`${BASE}/conversations`);
  return response.data.conversations;
}

export async function fetchSupportConversation(
  conversationId: string
): Promise<SupportConversationDTO> {
  const response = await getApiClient().get<{ data: SupportConversationDTO }>(
    `${BASE}/conversations/${encodeURIComponent(conversationId)}`
  );
  return response.data;
}

export async function postSupportAnswerFeedback(
  messageId: string,
  body: {
    rating: SupportAnswerRating;
    reason?: string;
    comment?: string;
  }
): Promise<void> {
  await getApiClient().post(
    `${BASE}/answers/${encodeURIComponent(messageId)}/feedback`,
    body
  );
}

export async function patchSupportConversation(
  conversationId: string,
  body: { status: SupportConversationStatus; escalationNote?: string }
): Promise<void> {
  await getApiClient().patch(
    `${BASE}/conversations/${encodeURIComponent(conversationId)}`,
    body
  );
}

export async function fetchSupportGaps(
  sinceDays: number
): Promise<SupportGapsDTO> {
  const response = await getApiClient().get<{ data: SupportGapsDTO }>(
    `${BASE}/gaps?days=${sinceDays}`
  );
  return response.data;
}
